// ImageCell — THE picture in a grid row, whatever the picture's source is.
//
// Alex, 2026-08-27, on the live demo: "We need the image on the demo. And one
// next step is to add image to all & move the image to left of each row. Because
// this will make the demo attractive." And the priority behind it, same day:
// "3D is a main part of the success. DB is more the hybrid backup."
//
// TWO SOURCES, ONE CELL, AND THE PRECEDENCE IS THE POINT.
//   1. an `item_images` row  — a URL somebody chose for this item: a baked demo
//      picture, a supplier photo, later an upload.
//   2. the cached Onshape thumbnail (`ThumbCell`) — automatic, but only for a
//      linked item, and only when one is already on disk.
// The stored picture WINS. That is item-images.js's own stated design ("Onshape's
// element thumbnail is a FALLBACK for linked items, not the mechanism"), and it
// is what lets a hand-picked photo override a model that renders as a grey box.
//
// ⚠ ONE FETCH FOR THE WHOLE TABLE, NOT ONE PER ROW. A catalog grid mounts
// hundreds of these at once; a cell that fetched its own row would open hundreds
// of connections to answer a question one query already answers. The map is
// loaded once per page load and shared by every cell on every screen — see
// useItemImages below.
//
// Renders NOTHING when there is no picture. An empty frame on the ~1,800 rows
// that have none would be noise, and noise is what the column is fighting.

import { useEffect, useState } from 'react';
import ThumbCell from './ThumbCell.jsx';
import HoverZoomImg from './HoverZoomImg.jsx';

// ── The shared map ─────────────────────────────────────────────────────────
// Module-level, so the second screen to ask pays nothing. Deliberately NOT
// revalidated: pictures are loaded by a script or a data session, not by the
// grid, so "stale until reload" is the honest lifetime and a poll would be a
// request per screen forever to catch a change that happens monthly.
// ⚠ Anything that WRITES a picture must call invalidateItemImages().
let _promise = null;

function loadItemImages() {
  if (_promise) return _promise;
  _promise = fetch('/api/item-images?kind=image')
    .then(r => (r.ok ? r.json() : []))
    // A failure here must cost the grid nothing but its pictures. The rows are
    // the content; the picture is the garnish.
    .catch(() => [])
    .then(rows => new Map((Array.isArray(rows) ? rows : []).map(r => [r.db_id, r.url])));
  return _promise;
}

// A writer in the app now exists (PictureDrop, 2026-10-15), so invalidating also
// tells every mounted cell to re-read — otherwise the picture just dropped would
// stay invisible until a reload. Still one request: the cells share the promise.
const _listeners = new Set();
export function invalidateItemImages() {
  _promise = null;
  for (const fn of _listeners) fn();
}

export function useItemImages() {
  const [map, setMap] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion(v => v + 1);
    _listeners.add(bump);
    return () => { _listeners.delete(bump); };
  }, []);
  useEffect(() => {
    let alive = true;
    loadItemImages().then(m => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, [version]);
  return map;
}

// ImageCell({ dbId, onshapeRef, size })
//   dbId       — 'fam:472' / 'prod:713' / 'line:2580'; the item_images key.
//                MAY BE AN ARRAY, first hit wins: a catalog line asks
//                ['line:2580', 'fam:472'] because a size of a part looks like
//                the part, and one family picture then covers all six of its
//                sizes instead of needing six. Falsy entries are skipped, so a
//                caller can build the list without branching.
//   onshapeRef — the fallback, when nothing is stored for any candidate
//
// ⚠ The map is read by the cell itself rather than passed in, and that is what
// keeps the column a ONE-LINE change at each call site. It costs nothing extra:
// every cell awaits the same module-level promise, so 600 rows are still one
// request. Threading it as a prop would put it in three useMemo dependency
// lists, which is exactly the kind of wiring a column definition should not need.
// A grid row (or a title icon) draws this at `size`, which is often smaller
// than the source picture — HoverZoomImg shows the same picture larger on
// hover (Alex, 2026-08-27: "is there a way of the image be bigger on hover").

//   natural    — keep the picture's own shape: `size` is the HEIGHT, the width follows
//                the picture up to `maxWidth` (default `size`, i.e. square as before).
//                For logos, which are often wide (Alex, 2026-10-20).
export default function ImageCell({ dbId, onshapeRef, size = 28, natural = false, maxWidth }) {
  const images = useItemImages();
  const candidates = Array.isArray(dbId) ? dbId : [dbId];
  const url = images ? candidates.reduce((hit, id) => hit || (id ? images.get(id) : null), null) : null;

  if (url) {
    return (
      <HoverZoomImg
        src={url}
        style={{
          ...(natural ? { height: size, width: 'auto', maxWidth: maxWidth || size } : { width: size, height: size }),
          objectFit: 'contain', borderRadius: 2,
          border: '1px solid var(--th-border-subtle)', background: 'var(--th-row-even)',
          display: 'block',
        }}
      />
    );
  }

  // ⚠ Only once the map has ARRIVED. Falling back while it is still loading would
  // flash the model thumbnail and then replace it with the stored picture.
  if (images && onshapeRef) return <ThumbCell onshapeRef={onshapeRef} size={size} />;
  return null;
}
